import { STATUS } from "@/constants/statuses";
import { rewardsApplicationLoggerToken } from "@apps/rewards/tokens";
import { applyTransaction } from "@datorama/akita";
import type { Observable } from "rxjs";
import {
    Subscription,
    catchError,
    distinctUntilChanged,
    interval,
    retry,
    switchMap,
    tap,
} from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { getLoadingStatus } from "../../../utils/statuses";
import RewardsB2bCompanyApiService from "../api_service/RewardsB2bCompanyService";
import { RewardsB2bCompanyPointBalanceEntity } from "../models/RewardsB2bCompanyEntities";
import RewardsB2bCompanyStore from "../store/RewardsB2bCompanyStore";
import type { RewardsB2bCompanyPointBalanceQueryParams } from "../types";

@singleton()
class RewardsB2bCompanyService {
    private subscribersCount = 0;
    private intervalRequestSubscription: Subscription | null = null;

    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly rewardsB2bCompanyApiService: RewardsB2bCompanyApiService,
        private readonly rewardsB2bCompanyStore: RewardsB2bCompanyStore
    ) {}

    public getB2bCompanyPointBalance(
        queryParams: RewardsB2bCompanyPointBalanceQueryParams
    ): Observable<RewardsB2bCompanyPointBalanceEntity> {
        this.logger.debug(
            "RewardsB2bCompanyService.getB2bCompanyPointBalance is started"
        );
        const status = this.rewardsB2bCompanyStore.getStatus();
        this.rewardsB2bCompanyStore.setStatus(getLoadingStatus(status));

        return this.rewardsB2bCompanyApiService
            .getB2bCompanyPointBalance(queryParams)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsB2bCompanyService.getB2bCompanyPointBalance is errored",
                        error
                    );
                    this.rewardsB2bCompanyStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsB2bCompanyService.getB2bCompanyPointBalance is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        this.rewardsB2bCompanyStore.update((state) => ({
                            ...state,
                            b2bCompanyPointBalance: value,
                        }));
                        this.rewardsB2bCompanyStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    private getB2bCompanyPointBalanceWithInterval(
        queryParams: RewardsB2bCompanyPointBalanceQueryParams
    ): Observable<RewardsB2bCompanyPointBalanceEntity> {
        return interval(5000).pipe(
            switchMap(() =>
                this.rewardsB2bCompanyApiService.getB2bCompanyPointBalance(
                    queryParams
                )
            ),
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsB2bCompanyService.getB2bCompanyPointBalanceWithInterval is errored",
                    error
                );
                this.rewardsB2bCompanyStore.setError(true);
                throw error;
            }),
            distinctUntilChanged(
                (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
            ),
            tap((value) => {
                this.logger.debug(
                    "RewardsB2bCompanyService.getB2bCompanyPointBalanceWithInterval is ready"
                );
                //Store Update
                this.rewardsB2bCompanyStore.update((state) => ({
                    ...state,
                    b2bCompanyPointBalance: value,
                }));
            }),
            retry({ delay: 1000 })
        );
    }

    public startIntervalRequests = (
        queryParams: RewardsB2bCompanyPointBalanceQueryParams
    ) => {
        if (!this.intervalRequestSubscription) {
            this.intervalRequestSubscription =
                this.getB2bCompanyPointBalanceWithInterval(
                    queryParams
                ).subscribe();
        }

        this.subscribersCount++;
    };

    public stopIntervalRequests = () => {
        if (this.intervalRequestSubscription && this.subscribersCount <= 1) {
            this.intervalRequestSubscription.unsubscribe();
            this.intervalRequestSubscription = null;
        }

        this.subscribersCount--;
    };
}

export default RewardsB2bCompanyService;
